/**
 * Message type mirrored from the backend's chat.py
 */

import { useEffect } from "react";
import type { UsageBlockReason, UsageMeter } from "@/app/usageMeterState";

export type WsError = {
    type: "error";
    detail: { error: string; conversation_id: number };
};

export type WsStatus = {
    type: "status";
    detail: "message_start" | "message_delta" | "message_stop" | "awaiting_llm" | "retry" | "awaiting_event_summarisation" | "awaiting_document_summarisation" | "streaming" | "conversation_locked";
    usage?: {
        cache_creation_input_tokens: number;
        cache_read_input_tokens: number;
        input_tokens: number;
        output_tokens: number;
    };
};

export type WsAction<T> = {
    type: "action";
    detail: T;
};

export type WsMessage = {
    type: "message";
    detail: {
        id?: number;
        conversation_id: number;
        content: string;
        role: "user" | "assistant";
        agent_id?: number;
        is_pre_prompt?: boolean;
        is_identity?: boolean;
        is_injection?: boolean;
        incomplete?: boolean;
        created_at?: any; // Replace `any` with the appropriate datetime type if needed
        tool_result?: any;
    };
};

export type WsToolInputPartial = {
    type: "tool_input_partial";
    detail: {
        partial_json: string;
        conversation_id: number;
    };
};

export type WsToolStart = {
    type: "tool_start";
    detail: {
        tool_name: string;
        tool_id: string;
        conversation_id: number;
    };
};

export type ChatObjectRef = { object_type: "artifact" | "event" | "tag" | "document"; object_id: number };

export type WsChatAction =
    | WsAction<{ tool: "set_agent_tool"; agent_id: number }>
    | WsAction<{ tool: "set_selected_objects_tool"; objects: ChatObjectRef[] }>
    | WsAction<{ tool: "artifact_variable_name_update"; artifact_id: number; variable_name: string }>
    | WsAction<{
          tool: "object_created";
          object_type: "artifact" | "event" | "tag" | "document";
          object_id: number;
          label: string;
          is_draft?: boolean;
          parent?: { type: "event" | "artifact" | "tag" | "document"; id: number } | null;
          select?: boolean;
      }>;

export type WsUsageMeter = {
    type: "usage_meter";
    detail: UsageMeter & { conversation_id: number; cycle_cost: number };
};

/** The run stopped at a tool-cycle boundary because spending is no longer allowed. */
export type WsUsageBlocked = {
    type: "usage_blocked";
    detail: { reason: UsageBlockReason; conversation_id: number };
};

/** Everything a chat run streams: the payload of a session `run_event`. */
export type ChatRunEvent = WsError | WsMessage | WsStatus | WsToolInputPartial | WsToolStart | WsChatAction | WsUsageMeter | WsUsageBlocked;

/**
 * WebSocket utility for Client Chat
 * Establishes a WebSocket connection to `/ws/chat`.
 */
export const clientChatWebSocket = async (onMessage: (message: WsMessage | WsError | WsStatus | WsToolInputPartial) => void, onError?: (error: Event) => void, onClose?: () => void) => {
    // Convert HTTP URL to WebSocket URL
    const wsUrl = window.location.origin.replace(/^http/, "ws") + "/api/chat/ws/chat";
    const ws = new WebSocket(wsUrl);

    await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve();
        ws.onerror = (event) => {
            if (onError) {
                onError(event);
            }
            reject(new Error("WebSocket connection failed."));
        };
    });

    ws.onmessage = (event) => {
        const data: WsMessage | WsError | WsStatus | WsToolInputPartial = JSON.parse(event.data);
        onMessage(data);
    };

    ws.onerror = (event) => {
        if (onError) {
            onError(event);
        } else {
            console.error("WebSocket error:", event);
        }
    };

    ws.onclose = () => {
        if (onClose) {
            onClose();
        }
    };

    return {
        send: (payload: { id?: number; prompt: string; msg_id?: number }) => {
            ws.send(JSON.stringify(payload));
        },
        close: () => {
            ws.close();
        },
    };
};

export const editRequestWebSocket = async (id: number, onMessage: (message: number | null) => void, wsObjectPath: "artifact" | "tag" | "agent" | "organisation" | "client", onClose?: () => void, organisationId?: number) => {
    let wsUrl = "";

    if (wsObjectPath == "client") wsUrl = window.location.origin.replace(/^http/, "ws") + `/api/users/ws/request-${wsObjectPath}-edit`;
    else wsUrl = window.location.origin.replace(/^http/, "ws") + `/api/${wsObjectPath}s/ws/request-${wsObjectPath}-edit`;

    const ws = new WebSocket(wsUrl);

    await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve();
    });

    ws.onmessage = (event) => {
        const data: number | null = JSON.parse(event.data);
        onMessage(data);
    };

    ws.onclose = () => {
        if (onClose) {
            onClose();
        }
    };

    ws.send(JSON.stringify({ id: id, lock: true, organisation_id: organisationId }));

    return {
        close: () => {
            ws.close();
        },
    };
};
